'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Menu, X, LogOut } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from './ui';

interface NavLink {
  label: string;
  href: string;
  key: string;
}

export default function MobileNav({ links }: { links: NavLink[] }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    await logout();
    setOpen(false);
  };

  return (
    <div className="md:hidden">
      <button
        onClick={() => setOpen(!open)}
        className="p-2 rounded-lg text-brand-text-muted hover:bg-brand-bg transition-colors"
        aria-label="Toggle menu"
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="flex flex-col">
            <div className="flex items-center justify-between px-6 h-16 border-b border-brand-border">
              <Link href="/" className="flex items-center gap-2.5 group">
                <AppLogo size={32} />
                <span className="font-semibold text-brand-text text-lg tracking-tight">
                  Spreadd<span className="text-brand-primary font-light">LMS</span>
                </span>
              </Link>
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg text-brand-text-muted hover:bg-brand-bg"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-col gap-1 p-6 bg-white border-b border-brand-border">
              {links.map((link) => (
                <Link
                  key={link.key}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 rounded-xl text-base font-medium text-brand-text hover:bg-brand-bg transition-colors"
                >
                  {link.label}
                </Link>
              ))}

              {user ? (
                <div className="mt-4 pt-4 flex flex-col gap-2 bg-brand-bg p-2 rounded-md">
                  <div className="px-4 mb-4">
                    <p className="text-xs text-brand-text-muted">Signed in as</p>
                    <p className="font-medium text-brand-text truncate">{user.fullname}</p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-sm font-semibold text-brand-error bg-brand-error/5 hover:bg-brand-error/10 transition-colors disabled:opacity-50"
                  >
                    <LogOut size={16} />
                    {isLoggingOut ? 'Signing out...' : 'Sign out'}
                  </Button>
                </div>
              ) : (
                <div className="mt-4 pt-4">
                  <Link
                    href="/auth/login"
                    onClick={() => setOpen(false)}
                    className="block w-full text-center px-4 py-3.5 rounded-xl bg-brand-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                  >
                    Sign in
                  </Link>
                </div>
              )}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
